if (!customElements.get('product-card-video')) {
  class ProductCardVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.link = this.closest('.product-featured-image-link');
      if (!this.video || !this.link || this.abortController) return;
      try {
        const sources = JSON.parse(this.querySelector('[data-video-sources]').content.textContent);
        this.sources = Array.isArray(sources) ? sources.filter((source) => source && typeof source.url === 'string' && source.url.trim()) : [];
      } catch (error) {
        return;
      }
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.video.muted = true;
      this.poster = this.querySelector('.product-card-video__poster');
      if (this.poster) {
        this.poster.addEventListener('load', () => this.classList.add('is-poster-ready'), options);
        if (this.poster.complete && this.poster.naturalWidth) this.classList.add('is-poster-ready');
      }
      this.link.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') this.start();
      }, options);
      this.link.addEventListener('pointerleave', () => this.stop(), options);
      this.link.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') this.start();
      }, options);
      this.link.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'touch') this.stop();
      }, options);
      this.link.addEventListener('pointercancel', () => this.stop(), options);
      this.link.addEventListener('focusin', () => this.start(), options);
      this.link.addEventListener('focusout', () => this.stop(), options);
      this.video.addEventListener('playing', () => {
        if (!this.active) return;
        this.classList.remove('is-loading');
        this.classList.add('is-playing');
      }, options);
      this.video.addEventListener('waiting', () => {
        if (this.active) this.classList.add('is-loading');
      }, options);
      this.video.addEventListener('ended', () => {
        if (!this.active) return;
        this.video.currentTime = 0;
        this.play();
      }, options);
      this.video.addEventListener('error', () => {
        if (!this.active) return;
        if (this.usingHls && this.mp4Source) {
          this.usingHls = false;
          this.playbackGeneration++;
          this.classList.remove('is-playing');
          this.classList.add('is-loading');
          this.video.src = this.mp4Source.url;
          this.play();
        } else {
          this.stop();
        }
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
      }, options);
      this.observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) this.stop();
      });
      this.observer.observe(this.link);
    }

    start() {
      if (this.active || document.hidden) return;
      if (ProductCardVideo.activePreview && ProductCardVideo.activePreview !== this) {
        ProductCardVideo.activePreview.stop();
      }
      const hlsSource = this.sources.find((source) => source.format === 'm3u8' || /mpegurl/i.test(source.mime_type));
      const mp4Sources = this.sources.filter((source) => source.format === 'mp4').sort((a, b) => a.width - b.width);
      const previewSources = mp4Sources.filter((source) => Math.max(source.width, source.height) <= 720);
      const candidates = previewSources.length ? previewSources : mp4Sources;
      const width = this.link.clientWidth * Math.min(window.devicePixelRatio || 1, 2);
      this.mp4Source = candidates.find((source) => source.width >= width) || candidates[candidates.length - 1];
      this.usingHls = Boolean(hlsSource && (this.video.canPlayType(hlsSource.mime_type || 'application/vnd.apple.mpegurl') || this.video.canPlayType('application/vnd.apple.mpegurl')));
      const source = this.usingHls ? hlsSource : this.mp4Source;
      if (!source) return;
      this.active = true;
      this.playbackGeneration = (this.playbackGeneration || 0) + 1;
      ProductCardVideo.activePreview = this;
      this.classList.add('is-loading');
      if (this.poster && !this.poster.hasAttribute('src')) this.poster.src = this.poster.dataset.src;
      this.video.src = source.url;
      this.play();
    }

    play() {
      const generation = this.playbackGeneration;
      const attempt = this.video.play();
      if (attempt) attempt.catch(() => {
        if (this.active && generation === this.playbackGeneration && !this.video.error) this.stop();
      });
    }

    stop() {
      this.active = false;
      this.playbackGeneration = (this.playbackGeneration || 0) + 1;
      this.classList.remove('is-playing', 'is-loading');
      if (ProductCardVideo.activePreview === this) ProductCardVideo.activePreview = null;
      if (this.video && this.video.hasAttribute('src')) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }
  }
  customElements.define('product-card-video', ProductCardVideo);
}
